import React, { ChangeEvent, useEffect, useRef, useState } from "react";
// External dependencies
import * as yup from "yup";
import { SubmitHandler, useForm } from "react-hook-form";
import MenuItem from "@mui/material/MenuItem";
import { Tooltip as ReactTooltip } from "react-tooltip";
// Internal Dependencies
import useCustomeDriveApi from "../../hooks/useCompanyDriveApi";
import { DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
// Components
import {
  Row,
  SubHeaderTitle,
  Wrapper,
  WrapperContent,
  WrapperSubHeader,
  WrapperCard,
  CustomDataTable,
  DialogComponent,
  InputField,
  InputFileUpload,
  PageOptions,
  CustomToolBar,
  LeftToolBar,
  OptionMenu,
  SearchToolBar,
} from "../../components/common";
import { TfiMoreAlt } from "react-icons/tfi";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
// Logos and assets
import { CiTrash } from "react-icons/ci";
import { CiImport } from "react-icons/ci";
import CustomButton from "../../components/common/CustomButton/CustomButton";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorMessage } from "../../utils/ErrorMessage";
import { toastMessage } from "../../components/commonFunctions/ToasterMessage";
import { SelectChangeEvent } from "@mui/material";
import { convertToSelectedTimeZoneIntoDate } from "../../utils/timeZoneUtil";

interface CompanyDriveProps {
  navUrl: string;
}


// interface SessionData {
//   sessionScope: {
//     NavSubMenuPermission: { menuURL: string; action: string }[];
//   };
// }

interface FormValues {
  docName: string;
  document: any;
  remark: string;
}

const CompanyDrive: React.FC<CompanyDriveProps> = ({ navUrl }) => {

  const [modal, setModal] = useState<boolean>(false);
  // Page Options state...
  const [pageOption, setPageOption] = useState<string>("15");
  const [searchValue, setSearchValue] = useState<string>("");
  const [openRowMenuId, setOpenRowMenuId] = useState<number | null>(null);
  const [focus, setFocus] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const timeZone = useSelector((state: any) => state.timeZone || "Asia/Kolkata");
  // const sessionScope = useSelector((state: { sessionData: SessionData }) => state?.sessionData?.sessionScope);
  // const drivePermission = sessionScope?.NavSubMenuPermission?.find(
  //   (subMenu) => subMenu.menuURL === "drive"
  // )?.action;

  // checkbox functionality...
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [disabledSaveBtn, setDisabledSaveBtn] = useState<boolean>(false);
const isOpen = openRowMenuId ! == null ? false : true;
  // const companyId = 64;

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Clicked outside the menu, so close it
        setOpenRowMenuId(null);
      }
    };

    // Attach the event listener when the menu is open
    if (openRowMenuId !== null) {
      document.addEventListener("click", handleOutsideClick);
    }

    // Cleanup the event listener when the component unmounts or when the menu is closed
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [openRowMenuId]);
  // Data Table Api...
  const {
    fetchDataTableApi,
    paginationInfo,
    driveData,
    loadingData,
    createDocument,
    fileDownload,
    deleteDriveRow,
  } = useCustomeDriveApi();

  const handleRowMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
      event.stopPropagation();
      setOpenRowMenuId(openRowMenuId == rowId ? null : rowId);
    };

  const CustomerColumns = [
    {
      field: "id",
      headerName: "Sr. No.",
      width: 40,
      defaultChecked: true,
      renderCell: (params:any) =>
        paginationInfo.page * paginationInfo.perpage + params.row.index,
    },
    {
      field: "Document Name",
      headerName: "Document Name",
      width: 120,
      defaultChecked: true,
      renderCell: (params:any) => (
        <div style={{ wordBreak: "break-word" }}>{params.row.docName}</div>
      ),
    },
    {
      field: "fileName",
      headerName: "File Name",
      width: 50,
      defaultChecked: true,
      renderCell: (params:any) => (
        <div className="common__textOverflow">
          <div
            className="common__toolTip"
            style={{
              display: params?.row?.fileName?.length < 16 ? "inline" : "block",
            }}
            data-tooltip-id={`tooltip-file-${params.row.companyDriveId}`}
          >
            {params.row.fileName?.split(".")[0].substring(0, 18)}
            {params.row.fileName?.split(".")[0]?.length > 18 ? "..." : "."}
            {params.row.fileName?.split(".")[1]}
          </div>
          {params.row.fileName?.length > 0 && (
            <ReactTooltip
              id={`tooltip-file-${params.row.companyDriveId}`}
              place="top"
              style={{ zIndex: 1, background: "#269BD5", maxWidth: "300px" }}
            >
              {params.row.fileName}
            </ReactTooltip>
          )}
        </div>
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 80,
      defaultChecked: true,
      renderCell: (params:any) => params.row.createdBy,
    },
    {
      field: "createdAt",
      headerName: "Created Date & Time",
      width: 10,
      defaultChecked: true,
      renderCell: (params:any) => convertToSelectedTimeZoneIntoDate(params.row.createdAt,timeZone),
    },
    {
      field: "remark",
      headerName: "Remarks",
      width: 10,
      defaultChecked: true,
      class: "custom__td_class",
      renderCell: (params:any) => (
        <div className="common__textOverflow">
          <div
            className="common__toolTip"
            data-tooltip-id={`tooltip-remark-${params.row.companyDriveId}`}
          >
            {params.row.remark}
          </div>

          {params.row.remark?.length > 0 && (
            <ReactTooltip
              id={`tooltip-remark-${params.row.companyDriveId}`}
              place="bottom-start"
              style={{
                zIndex: 1,
                background: "#269BD5",
                maxWidth: "250px",
                wordBreak: "break-word",
              }}
            >
              {params.row.remark}
            </ReactTooltip>
          )}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 10,
      defaultChecked: true,
      renderCell: (params:any) => (
        <div className="d-flex justify-content-center">
          <UncontrolledDropdown>
            <DropdownToggle
              tag="a"
              onClick={(event) =>
                handleRowMenuOpen(event, params.row.companyDriveId)
              }
              className={`btn btn-trigger action-btn dropdown-toggle btn-icon me-n1 ${
                  isOpen ? "show" : ""
                }`}
                aria-expanded={isOpen}
            >
              <TfiMoreAlt />
            </DropdownToggle>

           {openRowMenuId === params.row.companyDriveId  &&  <DropdownMenu  style={{
                  minWidth: "8rem",
                  zIndex: 9999,
                  display: isOpen ? "block" : "none",
                }}>
              <OptionMenu
                menuItems={menuItems(
                  params.row.companyDriveId,
                  params.row.fileName
                )}
              />
            </DropdownMenu>}
          </UncontrolledDropdown>
        </div>
      ),
    },
  ];
  // keyur start code
  const menuItems = (id: number, fileName: string) => {
    const handleDownload = () => {
      fileDownload(
        {
          id: id,
          // companyId: companyId,
        },
        fileName
      );
      setOpenRowMenuId(null);
    };
    const handleDelete = async () => {
      const res = await deleteDriveRow(
        [id],
        parseInt(pageOption),
        paginationInfo.page * parseInt(pageOption)
      );

      if (res?.data?.status) {
        toastMessage(res?.data?.response, "success");
      }
    };

    return (
      <>
        {/* {drivePermission?.split(",")?.includes("4") && ( */}
        <MenuItem onClick={() => handleDelete()}>
          <CiTrash className="common__icon" />
          Delete
        </MenuItem>
        {/* )} */}
       {fileName && <MenuItem onClick={() => handleDownload()}>
          <CiImport className="common__icon" />
          Download
        </MenuItem>}
      </>
    );
  };
  // keyur end code

  const fetchData = (searchValue: string, startIndex: number, pageLength: number) => {
    fetchDataTableApi({
      searchValue: searchValue,
      // companyId: companyId,
      length: pageLength,
      start: startIndex,
      draw: 1,
    });
  };

  useEffect(() => {
    fetchDataTableApi({
      searchValue: "",
      length: 15,
      start: 0,
      draw: 1,
    });
  }, []);

  const handleSearchData = (event: ChangeEvent<HTMLInputElement>) => {
    const searchValue = event && event.target.value.toLowerCase();
    setSearchValue(searchValue);
    setFocus(true);
    fetchData(
      searchValue,
      paginationInfo.page * parseInt(pageOption),
      parseInt(pageOption)
    );
  };

  const handlePageOptions = (customPageSize: number) => {
    fetchDataTableApi({
      searchValue: searchValue,
      length: customPageSize,
      start: 0,
      draw: 1,
      // companyId: 64,
      // companyId: companyId,
    });
  };

  const handleChangePageOption = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    const selectedValue = event.target.value;
    setPageOption(selectedValue.toString());
    handlePageOptions(parseInt(selectedValue.toString()));
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchDataTableApi({
      searchValue: searchValue,
      length: parseInt(pageOption),
      start: (page - 1) * parseInt(pageOption),
      draw: 1,
      // companyId: 64,
      // companyId: companyId,
    });
  };

  const DriveValidationSchema = yup.object().shape({
    docName: yup
      .string()
      .required("The Document Name is required.")
      .matches(
        /^[A-Za-z._&\-\s]+$/,
        "The Name can only consist of alphabetical, period (.), underscore (_), ampersand (&), and hyphen (-)."
      )
      .min(2, "The Document Name must be at least 2 characters")
      .max(50, "The Document Name must be at most 50 characters")
      .test(
        "not-only-whitespace",
        "The Document Name is required.",
        (value) => value.trim().length > 0
      ),
    remark: yup
      .string()
      .required("The remark is required.")
      .matches(
        /^[A-Za-z._&\-\s]+$/,
        "The remark can only consist of alphabetical, period (.), underscore (_), ampersand (&), and hyphen (-)."
      )
      .min(2, "The remark must be at least 2 characters")
      .max(100, "The remark must be at most 100 characters")
      .test(
        "not-only-whitespace",
        "The remark is required.",
        (value) => value.trim().length > 0
      ),
    document: yup.mixed().required("A file is required."),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<FormValues>({
    defaultValues: {
      docName: "",
      document: undefined,
      remark: "",
    },
    resolver: yupResolver(DriveValidationSchema),
    mode: "onChange",
  });

  const handleClose = () => {
    reset(); // Reset the form when the cancel button is clicked
    setModal(false);
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    // Disable the Save button
    setDisabledSaveBtn(true);

    try {
      const formData = new FormData();
      if(data.document && data.document[0]){
      formData.append("file", data.document[0]);
      formData.append("fileName", data.document[0].name);
      formData.append("documentSize", data.document[0].size.toString());
      }
      formData.append("docName", data.docName);
      formData.append("remark", data.remark);

      const res = await createDocument(formData);

      if (res?.status === 200) {
        reset();
        setModal(false);
        toastMessage(res?.data, "success");
      } else {
        toastMessage(res?.error?.data);
      }
      fetchDataTableApi({
        searchValue: "",
        length: parseInt(pageOption),
        start: paginationInfo.page * parseInt(pageOption),
        draw: 1,
        // companyId: companyId,
      });
    } catch (error) {
      toastMessage("Failed to create document. Please try again later.");
    } finally {
      // Enable the Save button after API call completes (success or error)
      setDisabledSaveBtn(false);
    }
  };

  const dialogContentInput = () => {
    return (
      <span
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <InputField
          labelName="Document Name"
          placeholder="Document Name"
          name="docName"
          register={register}
          // min={2}
          // maxLength={50}
          required
        />
        {ErrorMessage(errors?.docName?.message)}
        <InputFileUpload
          labelName="Add Document"
          placeholder="No file chosen"
          name="document"
          register={register}
          errors={errors?.document}
          acceptTypes={["jpg", "jpeg", "png", "pdf"]}
          required
        />
        <InputField
          labelName="Remarks"
          placeholder="Document Remark"
          name="remark"
          register={register}
          // min={2}
          // maxLength={100}
          required
        />
        {ErrorMessage(errors?.remark?.message)}
      </span>
    );
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    const updatedRows = isChecked
      ? driveData.map((row: { companyDriveId: any; }) => row.companyDriveId)
      : [];
    setSelectedRows(updatedRows);
  };

  const handleRowCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, rowIndex:number) => {
    const isChecked = event.target.checked;
    const updatedRows = [...selectedRows];
    if (isChecked) {
      updatedRows.push(rowIndex);
    } else {
      const index = updatedRows.indexOf(rowIndex);
      setSelectAll(isChecked);
      if (index !== -1) {
        updatedRows.splice(index, 1);
      }
    }
    setSelectedRows(updatedRows);
  };
  const handleDeleteBtn = async () => {
    const res = await deleteDriveRow(
      selectedRows,
      parseInt(pageOption),
      paginationInfo.page * parseInt(pageOption)
    );

    if (res?.data?.status) {
      toastMessage(res?.data?.response, "success");
      setSelectedRows([]);
    }
  };
  return (
    <>
      <Wrapper customStyle={{ height: "600px" }} navUrl={""}>
        <WrapperSubHeader>
          <div className="mr-auto">
            <SubHeaderTitle subTitle={navUrl} />
          </div>
        </WrapperSubHeader>
        <WrapperContent>
          <Row>
            <WrapperCard>
              <CustomToolBar>
                <LeftToolBar>
                  <div className="pageBtn" style={{ marginRight: "5px" }}>
                    <PageOptions
                      pageOption={pageOption}
                      handleChangePageOption={handleChangePageOption}
                    />
                  </div>
                  {selectedRows.length > 0 && (
                    <CustomButton
                      open={true}
                      onClick={handleDeleteBtn}
                      className="delete-btn"
                      btnName={"Delete"}
                    />
                  )}
                </LeftToolBar>
                <SearchToolBar className="search-toolbar">
                  <div className="search-bar">
                    <input
                      type="text"
                      placeholder="Search List"
                      className="search-input"
                      value={searchValue}
                      onChange={handleSearchData}
                      autoFocus={focus}
                    />
                    <SearchIcon className="search-icon" />
                  </div>
                  {/* {drivePermission?.split(",")?.includes("1") && ( */}
                  {/* // {true && ( */}
                  <div className="create-btn-container">
                    <button
                      className="common__create_btn create-btn"
                      onClick={() => setModal(!modal)}
                    >
                      <AddIcon className="add-mui" />
                    </button>
                  </div>
                  {/* // )} */}
                </SearchToolBar>
              </CustomToolBar>
              
              <CustomDataTable
                rows={driveData}
                columns={CustomerColumns}
                paginationInfo={paginationInfo}
                handlePageChange={handlePageChange}
                pageOption={pageOption}
                handleChangePageOption={handleChangePageOption}
                loading={loadingData}
                handleRowCheckboxChange={handleRowCheckboxChange}
                handleSelectAllChange={handleSelectAllChange}
                selectAll={selectAll}
                selectedRows={selectedRows}
                isCheckboxShow={true}
                // isCheckboxShow={drivePermission?.split(",")?.includes("4")}
                isBranchFieldExist={true}
                defaultChecker={() => {
                  return true;
                } }
                customTableHeight={"460px"} 
                />
            </WrapperCard>
          </Row>
        </WrapperContent>
      </Wrapper>
      <DialogComponent
        handleClose={handleClose}
        dialogTitle={"New Document"}
        openDialog={modal}
        submitBtnName="Save"
        dialogContent={dialogContentInput()}
        handleSubmitBtn={handleSubmit(onSubmit)}
        disabledSubmitBtn={
          errors.docName!=null ||
          errors.remark!=null ||
          !dirtyFields.docName ||
          !dirtyFields.remark ||
          !dirtyFields.document ||
          disabledSaveBtn
        }
      />
    </>
  );
};

export default CompanyDrive;
